import React, { useState, useEffect, useCallback } from 'react';
import {
  FolderKanban,
  Plus,
  Edit2,
  Trash2,
  Star,
  Images,
  AlertCircle,
  Eye,
  Upload,
} from 'lucide-react';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import Badge from '../components/Badge';
import ImageUploader from '../components/ImageUploader';
import { useToast } from '../components/Toast';
import { queries } from '../../lib/supabase/queries';
import { storage } from '../../lib/supabase/storage';
import { isSupabaseConfigured } from '../../lib/supabase/client';

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    short_description: '',
    description: '',
    category: 'Commercial',
    cover_image_url: '',
    project_url: '',
    status: 'draft',
    featured: false,
    sort_order: 0,
  });
  const [formSaving, setFormSaving] = useState(false);
  const [coverFile, setCoverFile] = useState(null);

  // Gallery multi-image state inside project edit modal
  const [projectImages, setProjectImages] = useState([]);
  const [uploadingGalleryImg, setUploadingGalleryImg] = useState(false);

  const toast = useToast();

  const categories = [
    'Commercial',
    'Residential',
    'Renovation',
    'Living Room',
    'Modular Kitchen',
    'Luxury Wardrobe',
    'Pooja Unit',
    'TV & Entertainment',
    'Turnkey Architecture',
  ];

  const loadProjects = useCallback(async () => {
    if (!isSupabaseConfigured) {
      setLoading(false);
      setError('Supabase connection is not configured in .env.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await queries.projects.getAll();
      setProjects(data);
    } catch (err) {
      console.error('Error loading projects:', err);
      setError(err.message || 'Failed to fetch projects from Supabase.');
      toast.error('Failed to load projects: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleOpenCreate = () => {
    setIsEditing(false);
    setSelectedProject(null);
    setCoverFile(null);
    setProjectImages([]);
    setFormData({
      title: '',
      slug: '',
      short_description: '',
      description: '',
      category: 'Commercial',
      cover_image_url: '',
      project_url: '',
      status: 'draft',
      featured: false,
      sort_order: projects.length + 1,
    });
    setModalOpen(true);
  };

  const handleOpenEdit = (project) => {
    setIsEditing(true);
    setSelectedProject(project);
    setCoverFile(null);
    setProjectImages(project.project_images || []);
    setFormData({
      title: project.title,
      slug: project.slug,
      short_description: project.short_description || '',
      description: project.description || '',
      category: project.category || 'Commercial',
      cover_image_url: project.cover_image_url || '',
      project_url: project.project_url || '',
      status: project.status || 'draft',
      featured: Boolean(project.featured),
      sort_order: project.sort_order || 0,
    });
    setModalOpen(true);
  };

  const handleOpenPreview = (project) => {
    setSelectedProject(project);
    setPreviewModalOpen(true);
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    if (!isEditing || !formData.slug) {
      const autoSlug = title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      setFormData((prev) => ({ ...prev, title, slug: autoSlug }));
    } else {
      setFormData((prev) => ({ ...prev, title }));
    }
  };

  // Quick 1-click status toggle
  const handleToggleStatus = async (project, e) => {
    e.stopPropagation();
    try {
      await queries.projects.toggleStatus(project.id, project.status);
      toast.success(
        `Project status changed to ${project.status === 'published' ? 'Draft' : 'Published'}`
      );
      loadProjects();
    } catch (err) {
      toast.error('Failed to toggle status: ' + err.message);
    }
  };

  // Quick 1-click featured toggle
  const handleToggleFeatured = async (project, e) => {
    e.stopPropagation();
    try {
      await queries.projects.toggleFeatured(project.id, project.featured);
      toast.success(`Project ${!project.featured ? 'marked as Featured' : 'unmarked from Featured'}`);
      loadProjects();
    } catch (err) {
      toast.error('Failed to toggle featured: ' + err.message);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Project title is required');
      return;
    }

    setFormSaving(true);
    try {
      let finalCoverUrl = formData.cover_image_url;

      // Upload cover file if selected
      if (coverFile) {
        const uploadRes = await storage.uploadFile('project-images', coverFile, 'covers');
        finalCoverUrl = uploadRes.publicUrl;
      }

      const payload = {
        ...formData,
        cover_image_url: finalCoverUrl,
        slug: formData.slug || formData.title.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      };

      if (isEditing && selectedProject) {
        await queries.projects.update(selectedProject.id, payload);
        toast.success('Project updated in Supabase');
      } else {
        await queries.projects.create(payload);
        toast.success('Project created in Supabase');
      }

      setModalOpen(false);
      loadProjects();
    } catch (err) {
      console.error('Error saving project:', err);
      toast.error('Failed to save project: ' + err.message);
    } finally {
      setFormSaving(false);
    }
  };

  // Multi-image upload for existing project
  const handleAddProjectImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedProject) return;

    setUploadingGalleryImg(true);
    try {
      const uploadRes = await storage.uploadFile('project-images', file, `gallery-${selectedProject.id}`);
      const newImg = await queries.projects.addImage(
        selectedProject.id,
        uploadRes.publicUrl,
        uploadRes.storagePath,
        file.name,
        projectImages.length + 1
      );
      setProjectImages((prev) => [...prev, newImg]);
      toast.success('Image attached to project');
      loadProjects();
    } catch (err) {
      toast.error('Failed to upload project image: ' + err.message);
    } finally {
      setUploadingGalleryImg(false);
    }
  };

  const handleDeleteProjectImage = async (imgId, storagePath) => {
    try {
      await queries.projects.deleteImage(imgId, storagePath);
      setProjectImages((prev) => prev.filter((img) => img.id !== imgId));
      toast.success('Image removed from project');
      loadProjects();
    } catch (err) {
      toast.error('Failed to delete image: ' + err.message);
    }
  };

  const handleDelete = async () => {
    if (!selectedProject) return;
    try {
      await queries.projects.delete(selectedProject.id);
      toast.success('Project deleted from Supabase');
      setConfirmOpen(false);
      setSelectedProject(null);
      loadProjects();
    } catch (err) {
      console.error('Error deleting project:', err);
      toast.error('Failed to delete: ' + err.message);
    }
  };

  const columns = [
    {
      header: 'Project Details',
      accessor: (row) => (
        <div className="flex items-center gap-3.5">
          {row.cover_image_url ? (
            <img
              src={row.cover_image_url}
              alt={row.title}
              className="w-12 h-12 rounded-xl object-cover border border-slate-800 shrink-0 shadow-sm"
            />
          ) : (
            <div className="w-12 h-12 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-500 font-bold text-xs shrink-0">
              IMG
            </div>
          )}
          <div className="min-w-0">
            <div className="font-bold text-sm text-slate-100 flex items-center gap-2">
              <span className="truncate">{row.title}</span>
              {row.featured && (
                <span className="p-1 rounded-md bg-amber-500/10 text-amber-400 border border-amber-500/20" title="Featured Project">
                  <Star size={11} className="fill-amber-400" />
                </span>
              )}
            </div>
            <div className="text-xs text-slate-400 truncate max-w-xs">
              {row.short_description || row.slug}
            </div>
          </div>
        </div>
      ),
    },
    {
      header: 'Category',
      accessor: (row) => (
        <span className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-800/80 text-slate-300 border border-slate-700/60">
          {row.category || 'Commercial'}
        </span>
      ),
    },
    {
      header: 'Status (Click to Toggle)',
      accessor: (row) => (
        <button
          onClick={(e) => handleToggleStatus(row, e)}
          className="group focus:outline-none transition active:scale-95"
          title="Click to toggle Published / Draft"
        >
          <Badge variant={row.status === 'published' ? 'success' : 'warning'}>
            {row.status === 'published' ? 'PUBLISHED' : 'DRAFT'}
          </Badge>
        </button>
      ),
    },
    {
      header: 'Sort Order',
      accessor: (row) => (
        <span className="font-mono text-xs text-slate-400">#{row.sort_order || 0}</span>
      ),
    },
    {
      header: 'Actions',
      className: 'text-right',
      accessor: (row) => (
        <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => handleOpenPreview(row)}
            className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition"
            title="Preview Details"
          >
            <Eye size={15} />
          </button>
          <button
            onClick={(e) => handleToggleFeatured(row, e)}
            className={`p-2 rounded-xl transition ${
              row.featured
                ? 'text-amber-400 bg-amber-500/10 hover:bg-amber-500/20'
                : 'text-slate-400 hover:text-amber-400 hover:bg-slate-800'
            }`}
            title={row.featured ? 'Featured on Top' : 'Mark as Featured'}
          >
            <Star size={15} className={row.featured ? 'fill-amber-400' : ''} />
          </button>
          <button
            onClick={() => handleOpenEdit(row)}
            className="p-2 rounded-xl text-slate-400 hover:text-[#9BB7A3] hover:bg-[#9BB7A3]/10 transition"
            title="Edit Project"
          >
            <Edit2 size={15} />
          </button>
          <button
            onClick={() => {
              setSelectedProject(row);
              setConfirmOpen(true);
            }}
            className="p-2 rounded-xl text-slate-400 hover:text-rose-400 hover:bg-rose-950/30 transition"
            title="Delete Project"
          >
            <Trash2 size={15} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Top Header Card */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-900/60 p-6 rounded-3xl border border-slate-800">
        <div>
          <h1 className="text-xl sm:text-2xl font-black text-white flex items-center gap-2.5">
            <FolderKanban size={24} className="text-[#9BB7A3]" />
            Projects & Portfolio Management
          </h1>
          <p className="text-xs sm:text-sm text-slate-400 mt-1">
            Manage portfolio showcases, categories, multiple gallery images, and live publication status.
          </p>
        </div>

        <button
          onClick={handleOpenCreate}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-[#9BB7A3] hover:bg-[#8AA692] text-slate-950 font-bold text-xs uppercase tracking-wider transition shadow-lg shadow-[#9BB7A3]/20 active:scale-95 shrink-0"
        >
          <Plus size={16} />
          <span>Add Project</span>
        </button>
      </div>

      {error && (
        <div className="p-4 rounded-2xl bg-rose-950/40 border border-rose-800/40 text-rose-300 text-xs flex items-center gap-2.5">
          <AlertCircle size={16} className="shrink-0 text-rose-400" />
          <span>{error}</span>
        </div>
      )}

      {/* Main DataTable */}
      <DataTable
        columns={columns}
        data={projects}
        loading={loading}
        searchKey="title"
        searchPlaceholder="Search projects by title..."
        filters={[
          {
            key: 'category',
            label: 'Category',
            options: categories.map((cat) => ({ label: cat, value: cat })),
          },
          {
            key: 'status',
            label: 'Status',
            options: [
              { label: 'Published', value: 'published' },
              { label: 'Draft', value: 'draft' },
            ],
          },
        ]}
        emptyIcon={<FolderKanban size={40} className="text-slate-600" />}
        emptyTitle="No Projects Found"
        emptyDescription="Create your first portfolio project to showcase on the public website."
        onAddNew={handleOpenCreate}
        addNewLabel="Add Project"
      />

      {/* Create / Edit Project Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => !formSaving && setModalOpen(false)}
        title={isEditing ? 'Edit Project' : 'Create New Portfolio Project'}
        maxWidth="max-w-3xl"
      >
        <form onSubmit={handleSave} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Project Title <span className="text-rose-400">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={handleTitleChange}
                placeholder="e.g., Luxury Penthouse Living Suite"
                className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-[#9BB7A3]"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                URL Slug
              </label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="luxury-penthouse-living-suite"
                className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-[#9BB7A3]"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Category
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Publication Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
              >
                <option value="draft">Draft (Private)</option>
                <option value="published">Published (Public)</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Sort Order
              </label>
              <input
                type="number"
                value={formData.sort_order}
                onChange={(e) =>
                  setFormData({ ...formData, sort_order: parseInt(e.target.value, 10) || 0 })
                }
                className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
              Short Summary
            </label>
            <input
              type="text"
              value={formData.short_description}
              onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
              placeholder="Brief tagline shown on hover cards"
              className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-[#9BB7A3]"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
              Detailed Description
            </label>
            <textarea
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Full architectural narrative, material finishes, and spatial design details..."
              className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-[#9BB7A3]"
            />
          </div>

          {/* Cover Image Uploader */}
          <div className="bg-slate-950/60 p-4 rounded-2xl border border-slate-800/80">
            <ImageUploader
              label="Project Main Cover Image"
              currentImageUrl={formData.cover_image_url}
              value={formData.cover_image_url}
              onImageSelected={(file) => setCoverFile(file)}
              onImageRemoved={() => {
                setCoverFile(null);
                setFormData((prev) => ({ ...prev, cover_image_url: '' }));
              }}
              onChange={(url) => setFormData((prev) => ({ ...prev, cover_image_url: url }))}
              recommendedSize="Recommended: 1600x1000 JPG/WEBP (up to 5MB)"
            />
          </div>

          {/* If Editing: Multi-Image Gallery Manager */}
          {isEditing && selectedProject && (
            <div className="bg-slate-950/60 p-4 rounded-2xl border border-slate-800/80 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                  <Images size={14} className="text-[#9BB7A3]" />
                  Project Gallery Shots ({projectImages.length})
                </span>
                <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs font-semibold transition">
                  {uploadingGalleryImg ? (
                    <span className="animate-pulse">Uploading...</span>
                  ) : (
                    <>
                      <Upload size={12} />
                      <span>+ Attach Image</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAddProjectImage}
                    disabled={uploadingGalleryImg}
                    className="hidden"
                  />
                </label>
              </div>

              {projectImages.length > 0 ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2.5 pt-2">
                  {projectImages.map((img) => (
                    <div
                      key={img.id}
                      className="relative group rounded-xl overflow-hidden aspect-square bg-slate-900 border border-slate-800"
                    >
                      <img
                        src={img.image_url}
                        alt="Project gallery"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => handleDeleteProjectImage(img.id, img.storage_path)}
                        className="absolute inset-0 bg-rose-950/80 text-rose-300 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                        title="Remove Image"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-slate-500 italic">
                  No additional gallery photos attached yet. Click "+ Attach Image" to upload.
                </p>
              )}
            </div>
          )}

          {/* Featured Checkbox */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="featured"
              checked={formData.featured}
              onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
              className="w-4 h-4 rounded border-slate-800 text-[#9BB7A3] focus:ring-[#9BB7A3] bg-slate-950 cursor-pointer"
            />
            <label htmlFor="featured" className="text-xs text-slate-300 font-semibold cursor-pointer">
              Pin as Featured Project (prioritized at the top of the portfolio showcase)
            </label>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-800">
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="px-4 py-2.5 rounded-xl text-xs font-semibold text-slate-400 hover:text-white transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={formSaving}
              className="px-6 py-2.5 rounded-xl bg-[#9BB7A3] hover:bg-[#8AA692] text-slate-950 font-bold text-xs uppercase tracking-wider transition shadow-lg shadow-[#9BB7A3]/20 disabled:opacity-50"
            >
              {formSaving ? 'Saving to Database...' : isEditing ? 'Update Project' : 'Create Project'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Preview Modal */}
      {selectedProject && previewModalOpen && (
        <Modal
          isOpen={previewModalOpen}
          onClose={() => setPreviewModalOpen(false)}
          title="Project Preview"
          maxWidth="max-w-2xl"
        >
          <div className="space-y-4">
            {selectedProject.cover_image_url && (
              <img
                src={selectedProject.cover_image_url}
                alt={selectedProject.title}
                className="w-full h-64 object-cover rounded-2xl border border-slate-800"
              />
            )}
            <div className="flex items-center justify-between">
              <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#9BB7A3]/10 text-[#9BB7A3]">
                {selectedProject.category}
              </span>
              <Badge variant={selectedProject.status === 'published' ? 'success' : 'warning'}>
                {selectedProject.status.toUpperCase()}
              </Badge>
            </div>
            <h2 className="text-xl font-bold text-white">{selectedProject.title}</h2>
            <p className="text-sm text-slate-300 leading-relaxed">
              {selectedProject.description || selectedProject.short_description || 'No detailed description.'}
            </p>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setSelectedProject(null);
        }}
        onConfirm={handleDelete}
        title="Delete Project"
        message={`Are you sure you want to delete project "${selectedProject?.title}"? All associated gallery assets will also be removed.`}
        confirmText="Delete Project"
        type="danger"
      />
    </div>
  );
}
